// Data access + response types for the admin billing endpoints. Pages stay
// thin; every billing fetch/mutation goes through here.
import type {
  AdjustmentDuration,
  AdjustmentType,
  BillingAdjustment,
  BillingCadence,
  BillingForecast,
  Invoice,
  InvoiceLine,
} from "@cms/types";
import type { ApiClient } from "./api";

export interface BillingSummaryResponse {
  mrr_pence: number;
  counts: Record<string, number>;
  past_due: PastDueRow[];
  recent_invoices: RecentInvoiceRow[];
}

export interface PastDueRow {
  id: string;
  business_name: string | null;
  billing_status: string;
  billing_past_due_since: string | null;
  dunning_stage: number | null;
  failed_total_pence: number | null;
}

export interface RecentInvoiceRow {
  id: string;
  tenant_id: string;
  number: string;
  status: string;
  total_pence: number;
  issued_at: string;
  paid_at: string | null;
  business_name: string | null;
}

export interface TenantBillingResponse {
  tenant: {
    id: string;
    business_name: string | null;
    plan: string;
    billing_cadence: BillingCadence;
    billing_status: string;
    billing_past_due_since: string | null;
    dunning_stage: number | null;
    gc_customer_id: string | null;
    gc_mandate_id: string | null;
    gc_subscription_id: string | null;
  };
  charge: {
    cadence: BillingCadence;
    lines: InvoiceLine[];
    subtotal_pence: number;
    vat_rate_bps: number;
    vat_pence: number;
    total_pence: number;
  } | null;
  subscription: {
    id: string;
    status: string;
    amount_pence: number | null;
    next_charge_date: string | null;
  } | null;
  invoices: Invoice[];
}

export function fetchBillingSummary(api: ApiClient): Promise<BillingSummaryResponse> {
  return api.request<BillingSummaryResponse>("/admin/billing/summary");
}

export function fetchTenantBilling(api: ApiClient, tenantId: string): Promise<TenantBillingResponse> {
  return api.request<TenantBillingResponse>(`/admin/billing/${encodeURIComponent(tenantId)}`);
}

export function createMandateLink(
  api: ApiClient,
  tenantId: string,
  successRedirectUrl: string
): Promise<{ redirect_flow_id: string; redirect_url: string }> {
  return api.request(`/admin/billing/${encodeURIComponent(tenantId)}/redirect-flow`, {
    method: "POST",
    body: JSON.stringify({ success_redirect_url: successRedirectUrl }),
  });
}

export function completeMandate(
  api: ApiClient,
  tenantId: string,
  redirectFlowId: string
): Promise<{ mandate_id: string; billing_status: string }> {
  return api.request(`/admin/billing/${encodeURIComponent(tenantId)}/redirect-flow/complete`, {
    method: "POST",
    body: JSON.stringify({ redirect_flow_id: redirectFlowId }),
  });
}

export function startSubscription(
  api: ApiClient,
  tenantId: string
): Promise<{ subscription_id: string; amount_pence: number }> {
  return api.request(`/admin/billing/${encodeURIComponent(tenantId)}/subscription`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function setClientCadence(
  api: ApiClient,
  tenantId: string,
  cadence: BillingCadence
): Promise<{ billing_cadence: BillingCadence }> {
  return api.request(`/admin/billing/${encodeURIComponent(tenantId)}/cadence`, {
    method: "PUT",
    body: JSON.stringify({ cadence }),
  });
}

export function cancelTenantSubscription(
  api: ApiClient,
  tenantId: string
): Promise<{ billing_status: string }> {
  return api.request(`/admin/billing/${encodeURIComponent(tenantId)}/subscription/cancel`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function createAdjustment(
  api: ApiClient,
  tenantId: string,
  amountPence: number,
  description: string
): Promise<{ payment_id: string; charge_date: string | null }> {
  return api.request(`/admin/billing/${encodeURIComponent(tenantId)}/adjustment`, {
    method: "POST",
    body: JSON.stringify({ amount_pence: amountPence, description }),
  });
}

export function fetchClientForecast(api: ApiClient, tenantId: string): Promise<BillingForecast> {
  return api.request<BillingForecast>(`/admin/billing/${encodeURIComponent(tenantId)}/forecast`);
}

export function setClientTerm(
  api: ApiClient,
  tenantId: string,
  start: string,
  months: number
): Promise<{ term: { start: string; months: number; end: string } }> {
  return api.request(`/admin/billing/${encodeURIComponent(tenantId)}/term`, {
    method: "PUT",
    body: JSON.stringify({ start, months }),
  });
}

export function clearClientTerm(api: ApiClient, tenantId: string): Promise<{ term: null }> {
  return api.request(`/admin/billing/${encodeURIComponent(tenantId)}/term`, { method: "DELETE" });
}

export interface NewRecurringAdjustment {
  line_kind: "plan" | "module";
  module_slug?: string;
  adjustment_type: AdjustmentType;
  value_bps?: number;
  value_pence?: number;
  duration_type: AdjustmentDuration;
  duration_months?: number;
  ends_on?: string;
}

export function createRecurringAdjustment(
  api: ApiClient,
  tenantId: string,
  adjustment: NewRecurringAdjustment
): Promise<{ adjustment: BillingAdjustment | null }> {
  return api.request(`/admin/billing/${encodeURIComponent(tenantId)}/adjustments`, {
    method: "POST",
    body: JSON.stringify(adjustment),
  });
}

export function cancelRecurringAdjustment(
  api: ApiClient,
  tenantId: string,
  adjustmentId: string
): Promise<{ cancelled: boolean }> {
  return api.request(
    `/admin/billing/${encodeURIComponent(tenantId)}/adjustments/${encodeURIComponent(adjustmentId)}`,
    { method: "DELETE" }
  );
}

export function adminInvoiceHtmlPath(tenantId: string, invoiceId: string): string {
  return `/admin/billing/${encodeURIComponent(tenantId)}/invoices/${encodeURIComponent(invoiceId)}/html`;
}
